import { getDesignCenters, parseNewDesign } from './utilsDesign';
import { getCityById } from './utilsGeo';

const DESIGN_ENDPOINTS = [
  process.env.REACT_APP_SERVICE_DESIGN,
  'https://hd.cafe/design',
].filter(Boolean);

const UNCERTAIN_KEYS = ['start', 'mid', 'end'];

export const isUncertainAvatar = (avatar, isMe = false) => Boolean(
  (avatar?.my || avatar?.isMe || isMe)
  && avatar?.dob_start
  && avatar?.dob_end
);

export const normalizeDob = (dob = '') => (
  typeof dob === 'string' ? dob.replace(' ', 'T') : ''
);

export const timeFromDob = (dob) => {
  const normalized = normalizeDob(dob);
  if (!normalized.includes('T')) return '';
  return normalized.split('T')[1].substring(0, 5);
};

export const getUncertainTimezone = (avatar) => (
  avatar?.aggr?.city?.timezone
  || avatar?.aggr?.geo?.timezone
  || avatar?.city?.timezone
  || null
);

export const getUncertainDobs = (avatar = {}) => ({
  start: normalizeDob(avatar.dob_start),
  mid: normalizeDob(avatar.dob),
  end: normalizeDob(avatar.dob_end),
});

export const getUncertainCacheKey = (avatar, isMe = false) => {
  if (!isUncertainAvatar(avatar, isMe)) return '';
  const dobs = getUncertainDobs(avatar);
  return [avatar.token, dobs.start, dobs.mid, dobs.end, getUncertainTimezone(avatar)].join('|');
};

const tagDesign = (design, key) => {
  if (!design || design.id === 'dummy') return null;
  return {
    ...design,
    id: `${design.id || 'design'}-${key}`,
  };
};

const fetchDesign = async (dob, tz, key) => {
  if (!dob || !tz) return null;
  for (const base of DESIGN_ENDPOINTS) {
    try {
      const designResponse = await fetch(`${base}/api/date?date=${dob}&zone=${tz}`);
      if (!designResponse.ok) continue;
      const parsed = parseNewDesign(await designResponse.json());
      const tagged = tagDesign(parsed, key);
      if (tagged) return tagged;
    } catch (error) {
      console.error('Error fetching uncertain design:', error);
    }
  }
  return null;
};

// Gate numbers only: a red activation in one chart still counts as "this gate is on".
const gatesOfDesign = (design) => {
  const gates = new Set();
  (design?.personality || []).forEach((planet) => gates.add(Number(planet.gate)));
  (design?.design || []).forEach((planet) => gates.add(Number(planet.gate)));
  (design?.gates || []).forEach((gate) => {
    if (gate?.gate != null) gates.add(Number(gate.gate));
  });
  return gates;
};

const gatesInEveryDesign = (designs = {}) => {
  const list = UNCERTAIN_KEYS.map((key) => designs[key]).filter((design) => design?.id && design.id !== 'dummy');
  if (!list.length) return new Set();
  const [first, ...rest] = list.map(gatesOfDesign);
  return new Set([...first].filter((gate) => rest.every((set) => set.has(gate))));
};

const centersOfDesign = (design) => new Set(
  design?.active_chakras?.length
    ? design.active_chakras
    : getDesignCenters(design?.gates || [])
);

const centersInEveryDesign = (designs = {}) => {
  const list = UNCERTAIN_KEYS.map((key) => designs[key]).filter((design) => design?.id && design.id !== 'dummy');
  if (!list.length) return [];
  const [first, ...rest] = list.map(centersOfDesign);
  return [...first].filter((center) => rest.every((set) => set.has(center)));
};

export const getIntersectionDesign = (designs = {}) => {
  const list = UNCERTAIN_KEYS.map((key) => designs[key]).filter((design) => design?.id && design.id !== 'dummy');
  if (list.length < 3) return null;

  const mid = designs.mid;
  const commonGates = gatesInEveryDesign(designs);

  const personality = (mid.personality || []).filter((planet) => commonGates.has(Number(planet.gate)));
  const designSide = (mid.design || []).filter((planet) => commonGates.has(Number(planet.gate)));

  if (!personality.length && !designSide.length) {
    return {
      ...mid,
      id: `${mid.id || 'common'}-uncertain`,
      personality: [],
      design: [],
      gates: [],
      channels: [],
      type: '',
      profile: [],
    };
  }

  const parsed = parseNewDesign({
    meta: mid.meta || { personality: mid.id, design: mid.id },
    personality,
    design: designSide,
  }, false, { allowInvalidProfile: true });

  if (!parsed || parsed.id === 'dummy') return null;
  return {
    ...parsed,
    id: `${mid.id || parsed.id}-uncertain`,
  };
};

// Midpoint first, so its full set of planets stays intact and the extra rows
// from start/end only add gates the midpoint doesn't have.
const collectSide = (designs, side) => {
  const seen = new Set();
  const planets = [];
  ['mid', 'start', 'end'].forEach((key) => {
    (designs[key]?.[side] || []).forEach((planet) => {
      if (seen.has(planet.gate)) return;
      seen.add(planet.gate);
      planets.push(planet);
    });
  });
  return planets;
};

// Union of all three charts: extra gates like 36 still complete 35-36 and
// define Solar Plexus. Color (prs/des) is ignored when deciding a gate is on.
export const getPotentialDesign = (designs = {}) => {
  const list = UNCERTAIN_KEYS.map((key) => designs[key]).filter((design) => design?.id && design.id !== 'dummy');
  if (list.length < 3) return null;

  const base = designs.mid || list[0];
  const personality = collectSide(designs, 'personality');
  const designSide = collectSide(designs, 'design');
  if (!personality.length && !designSide.length) return null;

  const parsed = parseNewDesign({
    meta: base.meta?.personality ? base.meta : { personality: base.id, design: base.id },
    personality,
    design: designSide,
  }, false, { allowInvalidProfile: true });

  if (!parsed || parsed.id === 'dummy') return null;
  return {
    ...parsed,
    id: `${base.id || parsed.id}-potential`,
  };
};

const planetSideKey = (planet, label) => `${label}:${planet}`;

const sidePlanets = (design, side) => {
  const map = new Map();
  (design?.[side] || []).forEach((planet) => {
    if (planet?.planet != null) map.set(planet.planet, String(planet.gate));
  });
  return map;
};

// Same planet + side, different gate across the time range.
export const getChangingPlanetKeys = (designs = {}) => {
  const list = UNCERTAIN_KEYS.map((key) => designs[key]).filter((design) => design?.id && design.id !== 'dummy');
  if (list.length < 2) return [];

  const changing = [];
  [['personality', 'prs'], ['design', 'des']].forEach(([side, label]) => {
    const maps = list.map((design) => sidePlanets(design, side));
    const names = new Set(maps.flatMap((map) => [...map.keys()]));
    names.forEach((planet) => {
      const gates = new Set(maps.map((map) => map.get(planet)).filter((gate) => gate != null));
      if (gates.size > 1) changing.push(planetSideKey(planet, label));
    });
  });
  return changing;
};

const resolveTimezone = async (avatar) => {
  const existing = getUncertainTimezone(avatar);
  if (existing) return existing;
  const cityId = avatar?.aggr?.city?.id ?? avatar?.city_id;
  if (!cityId) return null;
  const city = await getCityById(cityId);
  return city?.timezone || null;
};

export const fetchUncertainDesigns = async (avatar) => {
  const tz = await resolveTimezone(avatar);
  const dobs = getUncertainDobs(avatar);
  const storedMid = avatar?.design?.id && avatar.design.id !== 'dummy'
    ? tagDesign(avatar.design, 'mid')
    : null;

  const [start, mid, end] = await Promise.all([
    fetchDesign(dobs.start, tz, 'start'),
    fetchDesign(dobs.mid, tz, 'mid'),
    fetchDesign(dobs.end, tz, 'end'),
  ]);

  const designs = {
    start,
    mid: mid || storedMid,
    end,
  };

  // A derived design can be partial, so never let its parsing take the
  // fetched designs down with it.
  const derive = (fn) => {
    try {
      return fn(designs);
    } catch (error) {
      console.error('Error building uncertain design:', error);
      return null;
    }
  };

  return {
    designs,
    dobs,
    common: derive(getIntersectionDesign),
    potential: derive(getPotentialDesign),
    commonGates: [...gatesInEveryDesign(designs)],
    commonCenters: centersInEveryDesign(designs),
    changingPlanets: derive(getChangingPlanetKeys) || [],
  };
};

export const UNCERTAIN_KEYS_LIST = UNCERTAIN_KEYS;
